import { z } from "zod";
import { EmployeeStatusSchema, RemunerationTemplateThirteenthMonthUnitSchema } from "../../types/supabase.types.gen";
import { remunerationProfileIndemnityViewSchema } from "./remuneration-profile-indemnity";

export const REMUNERATION_PROFILE_TYPE = {
  FR: "fr",
} as const;

export type RemunerationProfileType = (typeof REMUNERATION_PROFILE_TYPE)[keyof typeof REMUNERATION_PROFILE_TYPE];

export const remunerationProfileTypeSchema = z.enum([REMUNERATION_PROFILE_TYPE.FR]);

export const REMUNERATION_PROFILE_ORIGIN = {
  STRUCTURED: "structured",
  LEGACY_IMPORT: "legacy_import",
} as const;

export type RemunerationProfileOrigin = (typeof REMUNERATION_PROFILE_ORIGIN)[keyof typeof REMUNERATION_PROFILE_ORIGIN];

export const remunerationProfileOriginSchema = z.enum([
  REMUNERATION_PROFILE_ORIGIN.STRUCTURED,
  REMUNERATION_PROFILE_ORIGIN.LEGACY_IMPORT,
]);

/** France-specific remuneration profile payload stored in JSONB. */
export const frenchRemunerationProfileDataSchema = z.object({
  profile_origin: remunerationProfileOriginSchema.optional(),
  hours_coefficient: z.number().min(1),
  bonus_coefficient: z.number().min(1),
  employee_status: EmployeeStatusSchema.nullable(),
  hourly_rate: z.number(),
  thirteenth_month_enabled: z.boolean(),
  thirteenth_month_unit: RemunerationTemplateThirteenthMonthUnitSchema.nullable(),
  thirteenth_month_amount: z.number().min(0).nullable().optional(),
  rtt_enabled: z.boolean(),
  rtt_weekly_hours: z.number().nullable(),
  overtime_enabled: z.boolean(),
  overtime_has_custom_convention: z.boolean().nullable(),
  overtime_rate_36_to_43_percent: z.number().nullable(),
  overtime_rate_above_43_percent: z.number().nullable(),
  indemnities: z.array(remunerationProfileIndemnityViewSchema),
});

export type FrenchRemunerationProfileData = z.infer<typeof frenchRemunerationProfileDataSchema>;

export const resolveRemunerationProfileOrigin = (
  profile: Pick<FrenchRemunerationProfileData, "profile_origin" | "indemnities">
): RemunerationProfileOrigin => {
  if (profile.profile_origin) {
    return profile.profile_origin;
  }

  return profile.indemnities.length > 0
    ? REMUNERATION_PROFILE_ORIGIN.STRUCTURED
    : REMUNERATION_PROFILE_ORIGIN.LEGACY_IMPORT;
};

export const isStructuredRemunerationProfileOrigin = (
  profileOrigin: RemunerationProfileOrigin | null | undefined
): boolean => profileOrigin === REMUNERATION_PROFILE_ORIGIN.STRUCTURED;
